import { queryOptions } from "@tanstack/solid-query";
import { axiosClient } from "~/js/network/axiosClient";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import type { IBaseCostGeneration } from "~/solidJs/entities/PremiumPopUp/helpers/IProducts";
import type { premiumCheckoutType } from "~/solidJs/entities/PremiumPopUp/types";
import type { UpgradePeriod } from "~/solidJs/widgets/ConfirmPlanChangeModal/model";
import type { UserSubscriptionPlanEnum } from "../helpers/types";
import { ApiErrorHandlers, BaseApi } from "./baseApi";

// Payment system enum
export enum PaymentSystem {
	Stripe = "stripe",
	YooKassa = "yookassa"
}

type Payment = {
	id: number;
	user_id: number;
	customer_id: number;
	price: number;
	discount_code: string;
	transaction_id: string;
	subscription_name: string;
};

export interface SavedPaymentMethod {
	id: string;
	payment_method_id: string;
	system: PaymentSystem | string;
	is_default: boolean;
	last4: string;
	brand: string;
}

export interface SavedPaymentMethodsResponse {
	payment_methods: SavedPaymentMethod[];
}

export interface OnDemandPlan {
	count_credits: number;
	api_label: string;
	front_price: string;
	front_price_ru: string;
}

export interface PlanFeature {
	id: string;
	label: string;
}

export interface PlanComparisonData {
	planName: string;
	features: PlanFeature[];
}

export interface PlanComparisonResponse {
	currentPlan: PlanComparisonData;
	targetPlan: PlanComparisonData;
}

export interface PlanChangeDetails {
	amount: number;
	nextPaymentDate: string; // ISO 8601 date string
}

export interface PlanChangeRequest {
	targetPlan: string;
	upgradeInterval: string;
}

export class PaymentApi extends BaseApi {
	static baseUrl = "api/payment";
	static getPaymentsUrl = "/api/users/payments_log";

	static getSavedPaymentMethodsOptions() {
		return queryOptions({
			queryKey: ["savedPaymentMethods"],
			queryFn: () => PaymentApi.getSavedPaymentMethods()
		});
	}

	static async changeUserPlan(props: { planId: string; startUpgrading: UpgradePeriod }) {
		const { data } = await axiosClient.axiosClient.post<{ redirect_url: string | null }>(
			"api/v2/payments/upgrade_credits_tariff",
			{
				start_upgrading: props.startUpgrading,
				new_tariff: props.planId
			}
		);
		return data;
	}

	static async getSavedPaymentMethods(): Promise<SavedPaymentMethod[]> {
		const { data } = await axiosClient.axiosClient.get<SavedPaymentMethodsResponse>(
			`/${PaymentApi.baseUrlV2}/payments/saved_payment_methods`
		);
		return data.payment_methods;
	}

	static async setDefaultPaymentMethod(id: string, currency?: string): Promise<OnDemandPlan> {
		const { data } = await axiosClient.axiosClient.post(
			`/${PaymentApi.baseUrlV2}/payments/set_default_payment_method`,
			{},
			{
				params: {
					payment_method_id: id,
					...(currency ? { currency } : {})
				}
			}
		);
		return data;
	}

	static getOnDemandPlansOptions() {
		return queryOptions({
			queryKey: ["onDemandPlans"],
			queryFn: () => PaymentApi.getOnDemandPlans()
		});
	}

	static async getOnDemandPlans(): Promise<{
		plans: OnDemandPlan[];
		base_cost_generation: IBaseCostGeneration[];
	}> {
		const { data } = await axiosClient.axiosClient.get<{
			plans: OnDemandPlan[];
			base_cost_generation: IBaseCostGeneration[];
		}>(`/${PaymentApi.baseUrlV2}/payments/on_demand_plans`);
		return data;
	}

	static async createCheckoutSession(props: {
		isTrial?: boolean;
		nameId: string;
		activeInterval: string;
		type?: premiumCheckoutType;
		promoCode?: string;
		clientId?: number;
	}) {
		const { data } = await axiosClient.axiosClient.post<{ url: string }>(
			"api/v2/payments/create-checkout-session",
			{
				subscription_name: props.nameId,
				length: props.activeInterval,
				...(props.type ? { kind: props.type } : {}),
				...(props.promoCode ? { promo_code: props.promoCode } : {})
			}
		);
		return data;
	}

	static async buyCredits(props: {
		api_label: string;
		system: string;
		payment_method_id: string;
	}) {
		try {
			const { data } = await axiosClient.axiosClient.post<{
				redirect_url?: string;
				status: string;
			}>(`/${PaymentApi.baseUrlV2}/payments/buy_credits`, props);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
		}
	}

	static async addNewPaymentMethod(props: { api_label: string; system: string }) {
		const { data } = await axiosClient.axiosClient.post<{
			redirect_url: string;
			status: string;
		}>(`/${PaymentApi.baseUrlV2}/payments/buy_credits`, { ...props, payment_method_id: null });
		return data;
	}

	static async createPortalSession() {
		const { data } = await axiosClient.axiosClient.post<{ url: string }>(
			"/api/v2/payments/create-portal-session"
		);
		return data;
	}

	static async cancelSubscription() {
		try {
			const { data } = await axiosClient.axiosClient.post<{ url: string }>(
				"/api/v2/front/modify_recurrent",
				{
					enable_recurrent_payments: false
				}
			);
			return data;
		} catch (e) {
			ApiErrorHandlers.handleBannedWordsAndLimitError(e);
		}
	}

	static async logTryUnsubscribe() {
		const { data } = await axiosClient.axiosClient.post<{ url: string }>(
			"/api/v2/front/try_unsubscribe"
		);
		return data;
	}

	static async getUserPayments() {
		const { data } = await axiosClient.axiosClient.get<Payment[]>(PaymentApi.getPaymentsUrl);
		return data;
	}

	static getPlanComparisonOptions(
		targetPlanType: UserSubscriptionPlanEnum,
		targetPlanName: string,
		currentPlanType?: UserSubscriptionPlanEnum,
		currentPlanName?: string
	) {
		return queryOptions({
			queryKey: ["planComparison", targetPlanType, currentPlanType, currentPlanName],
			queryFn: () =>
				PaymentApi.getPlanComparison(
					targetPlanType,
					targetPlanName,
					currentPlanType,
					currentPlanName
				)
		});
	}

	static async getPlanComparison(
		targetPlanType: UserSubscriptionPlanEnum,
		targetPlanName: string,
		currentPlanType?: UserSubscriptionPlanEnum,
		currentPlanName?: string
	): Promise<PlanComparisonResponse> {
		// Capture user store at call time to avoid stale data
		const userStore = getUserStore();

		// Use provided current plan (for teams) or fallback to user's personal plan
		const oldPlan = currentPlanType || userStore?.group || "free";
		const currentPlanDisplayName = currentPlanName || userStore?.subscription_name || "Free";

		const data = await axiosClient.axiosClient.post<{
			old_features: [
				{
					name: string;
					description: string;
				}
			];
			new_features: [
				{
					name: string;
					description: string;
				}
			];
		}>("/api/v2/payments/compare_plans", {
			new_plan: targetPlanType,
			old_plan: oldPlan
		});
		return {
			currentPlan: {
				planName: currentPlanDisplayName,
				features: data.data.old_features.map((feature) => ({
					id: feature.name,
					label: feature.name
				}))
			},
			targetPlan: {
				planName: targetPlanName,
				features: data.data.new_features.map((feature) => ({
					id: feature.name,
					label: feature.name
				}))
			}
		};
	}

	static getPlanChangeDetailsOptions(request: PlanChangeRequest) {
		return queryOptions({
			queryKey: ["planChangeDetails", request.targetPlan, request.upgradeInterval],
			queryFn: () => PaymentApi.getPlanChangeDetails(request)
		});
	}

	static async getPlanChangeDetails(request: PlanChangeRequest): Promise<PlanChangeDetails> {
		const res = await axiosClient.axiosClient.post<{
			upgrade_ts: string;
			cost: number;
			status: string;
		}>("/api/v2/payments/details_upgrade_tariff", {
			start_upgrading: request.upgradeInterval,
			new_tariff: request.targetPlan
		});
		return {
			amount: res.data.cost,
			nextPaymentDate: res.data.upgrade_ts
		};
	}
}
